import { useEffect, useState } from 'react';
import * as Sentry from '@sentry/react';

import {
  useSupabaseClient,
  useSupabaseReady,
} from '../supabase/SupabaseProvider';

const BUCKET = 'chat-attachments';
const SIGNED_URL_TTL_SECONDS = 60 * 60;
const REFRESH_LEEWAY_MS = 5 * 60 * 1000; // refresh 5 min before expiry

interface CachedSignedUrl {
  url: string;
  expiresAt: number;
}

// Module-level cache shared across every <AttachmentImage /> that
// references the same storage path. Without this, scrolling a long
// thread re-signs the same URLs repeatedly and the request count grows
// with re-renders.
const urlCache = new Map<string, CachedSignedUrl>();

function isCacheFresh(
  entry: CachedSignedUrl | undefined,
): entry is CachedSignedUrl {
  return !!entry && entry.expiresAt - Date.now() > REFRESH_LEEWAY_MS;
}

export function useAttachmentUrl(path: string | null | undefined) {
  const client = useSupabaseClient();
  const ready = useSupabaseReady();
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!client || !ready || !path) {
      setUrl(null);
      return;
    }

    let cancelled = false;

    const apply = (signed: string, expiresAt: number) => {
      if (cancelled) return;
      urlCache.set(path, { url: signed, expiresAt });
      setUrl(signed);
    };

    const sign = async () => {
      setLoading(true);
      try {
        const { data, error } = await client.storage
          .from(BUCKET)
          .createSignedUrl(path, SIGNED_URL_TTL_SECONDS);
        if (error || !data?.signedUrl) {
          if (error) {
            Sentry.captureException(error, {
              tags: { feature: 'comms', action: 'sign_attachment_url' },
              extra: { path },
            });
          }
          if (!cancelled) setUrl(null);
          return;
        }
        apply(data.signedUrl, Date.now() + SIGNED_URL_TTL_SECONDS * 1000);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    const cached = urlCache.get(path);
    if (isCacheFresh(cached)) {
      setUrl(cached.url);
    } else {
      void sign();
    }

    // Schedule a refresh just before the URL expires so long-lived chat
    // sessions don't render broken <img>s.
    const cachedNow = urlCache.get(path);
    const expiresAt =
      cachedNow?.expiresAt ?? Date.now() + SIGNED_URL_TTL_SECONDS * 1000;
    const refreshIn = Math.max(
      expiresAt - Date.now() - REFRESH_LEEWAY_MS,
      30_000,
    );
    const timer = setTimeout(() => {
      void sign();
    }, refreshIn);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [client, ready, path]);

  return { url, loading };
}
